import { ApolloClient } from 'apollo-client';
import { ApolloLink } from 'apollo-link';
import { HttpLink } from 'apollo-link-http';
import { InMemoryCache } from 'apollo-cache-inmemory';
import { onError } from 'apollo-link-error';
import { RetryLink } from 'apollo-link-retry';
import { router } from '../router/router';
import { setContext } from 'apollo-link-context';
import store from '../store';
import {
	getChooseSimulation,
	getLocalStorageData,
	getStorageReportSim,
	setLocalStorageData
} from './storageFunctions';
import { uniqBy } from 'lodash';
import { COOKIE_STORAGE_KEY, COOKIE_HEADER } from '@/helpers/constants';

const errorLink = onError(({ graphQLErrors, networkError }) => {
	if (graphQLErrors) {
		store.commit('setErrorMessages', uniqBy(graphQLErrors, 'message'));
		if (
			graphQLErrors.some(element => Object.keys(element).includes('entityGuid'))
		) {
			store.commit('setErrorMessagesGuids', graphQLErrors);
			// Hide loading of any error appears
			// that way the user will see the error message
			store.commit('setLoading', false);
		} else if (
			graphQLErrors.some(
				element =>
					Object.keys(element).includes('extensions') &&
					Object.keys(element.extensions).includes('data') &&
					Object.keys(element.extensions.data).includes('errorCode') &&
					element.extensions.data.errorCode == 'x0001'
			)
		) {
			store.commit('setLoading', false);
		}
	} else if (networkError) {
		if (networkError.statusCode == 401 || networkError.statusCode == 405) {
			router.push('/login');
		}
	}
});

const retryLink = new RetryLink({
	delay: {
		initial: 300,
		max: 1000,
		jitter: true
	},
	attempts: {
		max: 2,
		retryIf: error => {
			if (!error) return false;
			if (error.statusCode == 401 || error.statusCode == 405) return false;
			console.log('retrying');
			return true;
		}
	}
});

const responseInterceptor = new ApolloLink((operation, forward) => {
	return forward(operation).map(response => {
		const context = operation.getContext();
		const { headers } = context.response;
		const oldCookie = getLocalStorageData(COOKIE_STORAGE_KEY);
		const cookieHeader = headers.get(COOKIE_HEADER);

		// Add cookie header's data if it is different from already stored one
		if (headers && cookieHeader && cookieHeader !== oldCookie) {
			setLocalStorageData(COOKIE_STORAGE_KEY, cookieHeader);
		}

		// this is needed because we have to continue with next link in the chain
		return response;
	});
});

const requestInterceptor = setContext((_, { headers }) => {
	const srid = sessionStorage.getItem('srid');
	const ceUTC = getLocalStorageData(COOKIE_STORAGE_KEY);

	let studentViews = {};

	if (sessionStorage.getItem('viewAsStudent')) {
		const viewAsStudent = JSON.parse(
			sessionStorage.getItem('viewAsStudent'),
			true
		);

		studentViews = {
			simulationId: viewAsStudent.simulationId,
			isViewAsStudent: viewAsStudent.isViewAsStudent,
			demoStudentUniqueEmail: viewAsStudent.demoStudentUniqueEmail
		};
	}

	const simObject = getChooseSimulation();
	if (simObject.id) {
		headers = {
			...headers,
			simulationId: simObject.id,
			studentId: simObject.sid
		};
	}

	const simForReport = getStorageReportSim();
	if (simForReport) {
		headers = {
			...headers,
			studentId: simForReport.sid,
			simulationId: simForReport.id
		};
	}

	return {
		headers: {
			...studentViews,
			...headers,
			srid: srid,
			cookieExpiresUtc: ceUTC,
			studentFacilityId: sessionStorage.getItem('studentFacilityId'),
			pinauth: sessionStorage.getItem('pinAuthData')
		}
	};
});

// uncomment for console logging of graphql requests
/*const roundTripLink = new ApolloLink((operation, forward) => {
	operation.setContext({ start: new Date() });

	return forward(operation).map(data => {
		const time = new Date() - operation.getContext().start;
		if (operation.operationName) {
			console.log(`${time} ms to execute: ${operation.operationName}`);
		} else {
			console.log(`${time} ms to execute:`);
			console.log(operation);
		}

		return data;
	});
});*/

export default new ApolloClient({
	link: ApolloLink.from([
		requestInterceptor,
		// uncomment for console logging of graphql requests
		//roundTripLink,
		errorLink,
		retryLink,
		responseInterceptor,
		new HttpLink({
			uri: process.env.VUE_APP_API_URL + '/graphql',
			credentials: 'include'
		})
	]),
	cache: new InMemoryCache()
});
